<template>
  <section
    v-if="loaded"
    id="traffic-monitor-container"
  >
    <h1>
      {{ studioName }}<br>
      {{ currentDateTime.format('H:mm') }}&nbsp;の混雑状況
    </h1>
    <div id="traffic-monitor-status">
      {{ level }}
    </div>
    <chart
      :chart-data="chartData"
      :chart-options="chartOptions"
      :height="DEFAULT_HEIGHT"
      :styles="styles"
      :width="DEFAULT_WIDTH"
    />
    <p class="mt-2">※館内の3密状況を回避するため、混雑緩和のご協力をお願いいたします。</p>
  </section>
  <error-dialog
    v-else
    ref="errorDialog"
  />
</template>

<script>
import axios from 'axios'
import ErrorDialog from '@/components/ErrorDialog'
import Chart from '@/components/Chart'

export default {
  components: {
    ErrorDialog,
    Chart,
  },
  data() {
    return {
      loaded: false,
      studioName: '',
      currentDateTime: window.moment(),
      level: '',
      chartData: {
        labels: [],
        datasets: [
          {
            backgroundColor: [],
            data: [],
          },
        ],
      },
      chartOptions: {},
      styles: {},
    }
  },
  computed: {
    VIEW_HOURS_BETWEEN: () => [0, 23],
    DEFAULT_WIDTH: () => 568,
    DEFAULT_HEIGHT: () => 284,
    DEFAULT_X_MAX_TICKS_LIMIT: () => 24,
    DEFAULT_Y_MAX_TICKS_LIMIT: () => 2,
    RESIZE_X_MAX_TICKS_LIMIT_LIST: () => [
      {width: 768, limit: 24},
      {width: 479, limit: 12},
      {width: 277, limit: 8},
      {width: 209, limit: 6},
      {width: 175, limit: 5},
      {width: 164, limit: 4},
      {width: 152, limit: 3},
    ],
    CONGESTION_LEVEL_NOT_CROWDED_LABEL: () => '空いています',
    CONGESTION_LEVEL_NOT_CROWDED_COLOR: () => '#b0cfa2',
    CONGESTION_LEVEL_BIT_NOT_CROWDED_LABEL: () => 'やや空いています',
    CONGESTION_LEVEL_BIT_NOT_CROWDED_COLOR: () => '#b0cfa2',
    CONGESTION_LEVEL_BIT_CROWDED_LABEL: () => 'やや混雑しています',
    CONGESTION_LEVEL_BIT_CROWDED_COLOR: () => '#f5dd95',
    CONGESTION_LEVEL_CROWDED_LABEL: () => '混雑しています',
    CONGESTION_LEVEL_CROWDED_COLOR: () => '#e19594',
    CONGESTION_LEVEL_OVERTIME_LABEL: () => '時間外',
    CONGESTION_LEVEL_OVERTIME_COLOR: () => '#84b8e4',
    CONGESTION_LEVEL_LIST() {
      return [
        {label: this.CONGESTION_LEVEL_NOT_CROWDED_LABEL, color: this.CONGESTION_LEVEL_NOT_CROWDED_COLOR},
        {label: this.CONGESTION_LEVEL_BIT_NOT_CROWDED_LABEL, color: this.CONGESTION_LEVEL_BIT_NOT_CROWDED_COLOR},
        {label: this.CONGESTION_LEVEL_BIT_CROWDED_LABEL, color: this.CONGESTION_LEVEL_BIT_CROWDED_COLOR},
        {label: this.CONGESTION_LEVEL_CROWDED_LABEL, color: this.CONGESTION_LEVEL_CROWDED_COLOR},
        {label: this.CONGESTION_LEVEL_OVERTIME_LABEL, color: this.CONGESTION_LEVEL_NOT_CROWDED_COLOR},
      ]
    },
  },
  methods: {
    getCongestion() {
      axios.get(`${process.env.VUE_APP_API_DOMAIN}/v1/congestion/${this.$route.params.studioId}`)
        .then(res => {
          const congestion = res.data.data

          this.studioName = congestion.name
          this.level = congestion.level

          // 単一グラフ前提
          const datasets = this.chartData.datasets[0]

          // 全時刻の来場者数（配列）
          datasets.data = congestion.access_count

          // 現在時刻の来場者数
          datasets.data[this.currentDateTime.hours()] = congestion.count

          // 毎時の色設定
          for (let i = 0; i < datasets.data.length; i++) {
            datasets.backgroundColor[i] = i === this.currentDateTime.hours()
              ? this.barColorFromCongestionLevelLabel(congestion.level)
              : this.CONGESTION_LEVEL_OVERTIME_COLOR
          }

          // 混雑度の閾値（表示上）
          this.chartOptions.scales.y.max = congestion.congestion_line

          this.loaded = true
        })
        .catch(error => {
          this.$refs.errorDialog.open('エラーが発生しました', error.response.data.exceptionMessage)
        })
    },
    initializeChartDataLabels() {
      const beginHours = this.VIEW_HOURS_BETWEEN[0]
      const endHours = this.VIEW_HOURS_BETWEEN[1]

      for (let i = beginHours; i <= endHours; i++) {
        this.chartData.labels.push(`${i}時`)
      }
    },
    initializeChartOptions() {
      this.chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              title: function (context) {
                return null
              },
              label: function (context) {
                return context.label
              }
            }
          }
        },
        scales: {
          x: {
            display: true,
            ticks: {
              maxTicksLimit: this.DEFAULT_X_MAX_TICKS_LIMIT,
            },
            grid: {
              display: false,
            },
          },
          y: {
            display: true,
            position: 'right',
            ticks: {
              color: this.CONGESTION_LEVEL_CROWDED_COLOR,
              font: {
                weight: 'bold',
              },
              callback: function (value, index) {
                if (index % 2) {
                  return '混雑'
                }

                return null
              },
              maxTicksLimit: this.DEFAULT_Y_MAX_TICKS_LIMIT,
            },
            grid: {
              color: this.CONGESTION_LEVEL_CROWDED_COLOR,
              borderDash: [5, 5],
              drawBorder: false,
              drawTicks: false,
            },
          },
        },
      }
    },
    handleResize() {
      const windowWidth = window.innerWidth

      this.styles = {
        width: `${this.DEFAULT_WIDTH}px`,
        height: `${this.DEFAULT_HEIGHT}px`,
      }

      this.chartOptions.scales.x.ticks.maxTicksLimit = this.DEFAULT_X_MAX_TICKS_LIMIT

      // 現在の画面サイズに応じてグラフのサイズを変更（レスポンシブ対応）
      this.RESIZE_X_MAX_TICKS_LIMIT_LIST.forEach(item => {
        if (windowWidth < item.width) {
          this.styles = {
            width: `${windowWidth}px`,
            height: `${windowWidth / 2}px`,
          }

          this.chartOptions.scales.x.ticks.maxTicksLimit = item.limit
        }
      })
    },
    barColorFromCongestionLevelLabel(congestionLevelLabel) {
      const congestion = this.CONGESTION_LEVEL_LIST.find(item => {
        return item.label === congestionLevelLabel
      })

      return congestion ? congestion.color : this.CONGESTION_LEVEL_OVERTIME_COLOR
    },
  },
  created() {
    // グラフを初期化
    this.initializeChartDataLabels()
    this.initializeChartOptions()

    // 現在の画面サイズからグラフのサイズを設定
    this.handleResize()

    // 混雑状況を取得
    this.getCongestion()
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.addEventListener('resize', this.handleResize)
  },
}
</script>

<style
  lang="scss"
  scoped
>
#traffic-monitor-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;

  @media screen and (min-width: 768px) and (min-height: 768px) {
    & {
      width: 600px;
    }
  }

  & h1 {
    font-size: 16px;
    font-weight: normal;
    text-align: center;
    margin: 0;
  }

  & #traffic-monitor-status {
    font-size: 24px;
    font-weight: bold;
    text-align: center;
    margin: 16px 0;
  }
}
</style>
